import type { SpecContent } from "@szyos/contracts";
import { MAX_DONE_CRITERIA } from "./focus";

/**
 * Bestandsaufnahme lesen (UX-11): docs/BESTANDSAUFNAHME.md hat die Abschnitte Stand, Fertig heißt, Später,
 * Nächster Schritt und Empfehlung. Daraus übernimmt der Owner Kriterien ins Projekt und macht den nächsten
 * Schritt mit einem Tipp zum Auftrag. Die Agenten schreiben frei; der Parser ist deshalb nachsichtig.
 */
export const STOCKTAKE_PATH = "docs/BESTANDSAUFNAHME.md";

const MAX_TEXT = 300;

export interface StocktakeNextStep {
  title: string;
  /** Erster Absatz vollständig, falls der Schritt keine eigene Aufgabenliste hat. */
  detail: string;
  why: string;
  tasks: string[];
  doneWhen: string[];
}

export interface StocktakeDoc {
  /** Fließtext je Abschnitt, Überschrift ohne `##`. */
  sections: { heading: string; body: string }[];
  done: string[];
  later: string[];
  nextStep: StocktakeNextStep | null;
  recommendation: string | null;
}

const key = (heading: string) =>
  heading
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z]/g, "");

/** Markdown-Auszeichnung entfernen, damit Kriterien und Titel als schlichter Text gespeichert werden. */
export function plainText(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function clip(text: string, max = MAX_TEXT): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

/** Kurzer Titel für einen Auftrag: an einer Wortgrenze gekürzt. */
function shortTitle(text: string, max = 100): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  return `${cut
    .slice(0, Math.max(cut.lastIndexOf(" "), 40))
    .replace(/[,:;(]$/, "")
    .trimEnd()}…`;
}

/** Fett gesetzter Anfang eines Eintrags („**200 Wörter live.** Danach …“) oder der erste Satz. */
function lead(item: string): string {
  const bold = /^\*\*(.+?)\*\*/.exec(item.trim());
  const text = bold?.[1] ?? plainText(item).split(/(?<=[.!?])\s/)[0] ?? item;
  return clip(plainText(text).replace(/[.:]$/, ""));
}

/** Einträge einer Liste der obersten Ebene; Fortsetzungszeilen gehören zum Eintrag davor. */
function listItems(body: string): string[] {
  const items: string[] = [];
  for (const line of body.split("\n")) {
    const m = /^(?:[-*]|\d+[.)])\s+(.*)$/.exec(line);
    if (m?.[1]) items.push(m[1]);
    else if (/^\s{2,}\S/.test(line) && items.length > 0) items[items.length - 1] += ` ${line.trim()}`;
  }
  return items;
}

function paragraphs(body: string): string[] {
  return body
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function parseNextStep(body: string): StocktakeNextStep | null {
  const paras = paragraphs(body);
  if (paras.length === 0) return null;
  const first = paras[0] ?? "";
  const title = lead(first);
  const why = paras.find((p) => /^warum/i.test(plainText(p)));
  const done = paras.find((p) => /^(ergebnis|erledigt|fertig, wenn|woran)/i.test(plainText(p)));
  const doneText = done ? plainText(done).replace(/^[^:]*:\s*/, "") : "";
  return {
    title,
    detail: clip(plainText(first), 500),
    why: why ? clip(plainText(why).replace(/^warum[^:]*:\s*/i, ""), 1500) : "",
    tasks: listItems(body).map((t) => clip(plainText(t), 500)),
    doneWhen: doneText
      .split(/(?<=[.!?])\s+(?=[A-ZÄÖÜ`„])/)
      .map((s) => clip(s.trim(), 500))
      .filter(Boolean),
  };
}

export function parseStocktake(markdown: string): StocktakeDoc {
  const sections: { heading: string; body: string }[] = [];
  let current: { heading: string; lines: string[] } | null = null;
  for (const line of markdown.replace(/\r\n/g, "\n").split("\n")) {
    const h = /^##\s+(.+?)\s*#*$/.exec(line);
    if (h?.[1]) {
      if (current) sections.push({ heading: current.heading, body: current.lines.join("\n").trim() });
      current = { heading: h[1].trim(), lines: [] };
    } else if (current) {
      current.lines.push(line);
    }
  }
  if (current) sections.push({ heading: current.heading, body: current.lines.join("\n").trim() });

  const find = (...names: string[]) => sections.find((s) => names.includes(key(s.heading)))?.body ?? null;
  const done = find("fertigheisst", "fertigheisstkriterien");
  const later = find("spaeter");
  const next = find("naechsterschritt");
  const rec = find("empfehlung");
  return {
    sections,
    done: done ? listItems(done).map(lead).filter(Boolean) : [],
    later: later
      ? listItems(later)
          .map((t) => clip(plainText(t)))
          .filter(Boolean)
      : [],
    nextStep: next ? parseNextStep(next) : null,
    recommendation: rec ? clip(plainText(paragraphs(rec).slice(0, 2).join(" ")), 1500) : null,
  };
}

/**
 * Kriterien und „Später“ aus der Bestandsaufnahme mit dem Stand des Projekts zusammenführen: vorhandene
 * bleiben, neue kommen dazu, bis sieben Kriterien voll sind; der Rest landet unter „Später“.
 */
export function adoptDone(
  existing: { text: string; met: boolean }[],
  existingLater: string[],
  doc: Pick<StocktakeDoc, "done" | "later">,
): { criteria: { text: string; met: boolean }[]; later: string[] } {
  const known = new Set(existing.map((c) => c.text));
  const fresh = doc.done.filter((t) => !known.has(t));
  const room = Math.max(0, MAX_DONE_CRITERIA - existing.length);
  const criteria = [...existing, ...fresh.slice(0, room).map((text) => ({ text, met: false }))];
  const laterSet = new Set(existingLater);
  const later = [...existingLater];
  for (const t of [...fresh.slice(room), ...doc.later]) {
    if (!laterSet.has(t) && later.length < 50) {
      laterSet.add(t);
      later.push(t);
    }
  }
  return { criteria, later };
}

/** Briefing für den nächsten Schritt; der Owner gibt es nur noch frei. */
export function nextStepSpec(
  project: { title: string; goal: string },
  doc: StocktakeDoc,
): { title: string; spec: SpecContent } | null {
  const step = doc.nextStep;
  if (!step) return null;
  const title = shortTitle(step.title);
  const problem = [
    `Nächster Schritt laut Bestandsaufnahme (${STOCKTAKE_PATH}): ${step.title}.`,
    step.why ? `Warum: ${step.why}` : "",
    doc.recommendation ? `Empfehlung der Bestandsaufnahme: ${doc.recommendation}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
  return {
    title,
    spec: {
      problem: clip(problem, 4000),
      audience: clip(project.goal || project.title, 2000),
      mustHaves: (step.tasks.length > 0 ? step.tasks : [step.detail || step.title]).slice(0, 50),
      nonGoals: [
        `Nichts aus „Später“ in ${STOCKTAKE_PATH}.`,
        "Keine Änderungen außerhalb dessen, was die Muss-Punkte nennen.",
      ],
      designNotes: `Vor der Arbeit ${STOCKTAKE_PATH} lesen (Stand, Fertig heißt, Nächster Schritt) und die dort genannten Belege nutzen. Ändert sich der Stand, den Abschnitt „Stand“ dort nachführen.`,
      acceptance: (step.doneWhen.length > 0 ? step.doneWhen : [`Erledigt: ${step.title}`]).slice(0, 50),
    },
  };
}
